import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './utils/eligibilityStatusIcons.css';
import Layout from './components/layout/Layout';
import EligibilityRefreshProgress from './components/EligibilityRefreshProgress';
import Dashboard from './pages/Dashboard';
import Schools from './pages/Schools';
import SchoolsUS from './pages/SchoolsUS';
import SchoolProfile from './components/schools/SchoolProfile';
import SchoolProfileUS from './components/schools/SchoolProfileUS';
import UserProfile from './pages/UserProfile';
import UserStudentProfile from './pages/UserStudentProfile';
import Students from './pages/Students';
import StudentProfileForGuidance from './components/students/StudentProfileForGuidance';
import GuidanceDashboard from './pages/GuidanceDashboard';

import Login from './pages/Login';
import Programs from './pages/Programs';
import ProgramProfilePage from './pages/ProgramProfile';
import Events from './pages/Events';
import NewEvent from './pages/NewEvent';
import Scholarships from './pages/Scholarships';

import EventDetail from './pages/EventDetail';
import ScholarshipDetail from './pages/ScholarshipDetail';
import ProgramCategories from './pages/ProgramCategories';
import NewsfeedList from './pages/NewsfeedList';
import AdvancedSearch from './pages/AdvancedSearch';
import Admin from './pages/Admin';
import AddEditSchools from './pages/AddEditSchools';
import AddEditEvents from './pages/AddEditEvents';
import Edvisor from './pages/Edvisor';
import Reports from './pages/Reports';
import ReferralLink from './pages/ReferralLink';
import Resources from './pages/Resources';
import Support from './pages/Support';
import AdminSupport from './pages/AdminSupport';
import AdminUsers from './pages/AdminUsers';
import ReferralRedirect from './components/ReferralRedirect';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { ReportsRoute } from './components/ReportsRoute';
import { GuidanceRoute } from './components/GuidanceRoute';

import { EventsProvider } from './contexts/EventsContext';
import { UserProvider } from './contexts/UserContext';
import { SchoolsProvider } from './contexts/SchoolsContext';
import { SchoolsProviderUS } from './contexts/SchoolsContextUS';
import { ProgramsProvider } from './contexts/ProgramsContext';
import { ProgramCategoriesProvider } from './contexts/ProgramCategoriesContext';
import LazyScholarshipsProvider from './components/LazyScholarshipsProvider';
import { PrerequisiteProvider } from './contexts/PrerequisiteContext';
import { CoursePrerequisiteProvider } from './contexts/CoursePrerequisiteContext';
import { DebugProvider } from './contexts/DebugContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { EdvisorProvider } from './contexts/EdvisorContext';
import { GuidanceStudentsProvider } from './contexts/GuidanceStudentsContext';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const App: React.FC = () => {
  return (
    <UserProvider>
      <OnboardingProvider>
        <GuidanceStudentsProvider>
          <EventsProvider>
            <SchoolsProvider>
              <SchoolsProviderUS>
                <PrerequisiteProvider>
                  <CoursePrerequisiteProvider>
                    <ProgramsProvider>
                      <ProgramCategoriesProvider>
                        <EdvisorProvider>
                        <DebugProvider>
                        <ThemeProvider theme={theme}>
                          <CssBaseline />
                          <Router>
                            <LazyScholarshipsProvider>
                              <Layout>
                      <Routes>
                        {/* Public routes */}
                        <Route path="/login" element={<Login />} />
                        
                        {/* Protected routes */}
                        <Route path="/" element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        } />

                        <Route path="/schools" element={
                          <ProtectedRoute>
                            <Schools />
                          </ProtectedRoute>
                        } />
                        <Route path="/schools-us" element={
                          <ProtectedRoute>
                            <SchoolsUS />
                          </ProtectedRoute>
                        } />
                        <Route path="/schools-us/:id" element={
                          <ProtectedRoute>
                            <SchoolProfileUS />
                          </ProtectedRoute>
                        } />
                        <Route path="/schools/:id" element={
                          <ProtectedRoute>
                            <SchoolProfile />
                          </ProtectedRoute>
                        } />

                        <Route path="/user-profile" element={
                          <ProtectedRoute>
                            <UserProfile />
                          </ProtectedRoute>
                        } />
                        <Route path="/my-student-profile" element={
                          <ProtectedRoute>
                            <UserStudentProfile />
                          </ProtectedRoute>
                        } />
                        <Route path="/guidance-dashboard" element={
                          <GuidanceRoute>
                            <GuidanceDashboard />
                          </GuidanceRoute>
                        } />
                        <Route path="/students" element={
                          <GuidanceRoute>
                            <Students />
                          </GuidanceRoute>
                        } />
                        <Route path="/students/:studentId" element={
                          <GuidanceRoute>
                            <StudentProfileForGuidance />
                          </GuidanceRoute>
                        } />
                        <Route path="/programs" element={
                          <ProtectedRoute>
                            <Programs />
                          </ProtectedRoute>
                        } />
                        <Route path="/programs/:id" element={
                          <ProtectedRoute>
                            <ProgramProfilePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/events" element={
                          <ProtectedRoute>
                            <Events />
                          </ProtectedRoute>
                        } />
                        <Route path="/events/new" element={
                          <ProtectedRoute>
                            <NewEvent />
                          </ProtectedRoute>
                        } />
                        <Route path="/events/:id" element={
                          <ProtectedRoute>
                            <EventDetail />
                          </ProtectedRoute>
                        } />
                        <Route path="/newsfeed" element={
                          <ProtectedRoute>
                            <NewsfeedList />
                          </ProtectedRoute>
                        } />
                        <Route path="/scholarships" element={
                          <ProtectedRoute>
                            <Scholarships />
                          </ProtectedRoute>
                        } />
                        <Route path="/scholarships/:id" element={
                          <ProtectedRoute>
                            <ScholarshipDetail />
                          </ProtectedRoute>
                        } />

                        <Route path="/program-categories" element={
                          <ProtectedRoute>
                            <ProgramCategories />
                          </ProtectedRoute>
                        } />
                        
                        <Route path="/advanced-search" element={
                          <ProtectedRoute>
                            <AdvancedSearch />
                          </ProtectedRoute>
                        } />
                        
                        <Route path="/admin" element={
                          <AdminRoute>
                            <Admin />
                          </AdminRoute>
                        } />
                        
                        <Route path="/admin/add-edit-schools" element={
                          <AdminRoute>
                            <AddEditSchools />
                          </AdminRoute>
                        } />
                        
                        <Route path="/admin/add-edit-events" element={
                          <AdminRoute>
                            <AddEditEvents />
                          </AdminRoute>
                        } />
                        
                        <Route path="/reports" element={
                          <ReportsRoute>
                            <Reports />
                          </ReportsRoute>
                        } />
                        
                        <Route path="/admin/support" element={
                          <ReportsRoute>
                            <AdminSupport />
                          </ReportsRoute>
                        } />
                        
                        <Route path="/admin/users" element={
                          <ReportsRoute>
                            <AdminUsers />
                          </ReportsRoute>
                        } />
                        
                        <Route path="/edvisor" element={
                          <ProtectedRoute>
                            <Edvisor />
                          </ProtectedRoute>
                        } />
                        
                        <Route path="/referral-link" element={<ReferralLink />} />
                        
                        <Route path="/resources" element={
                          <GuidanceRoute>
                            <Resources />
                          </GuidanceRoute>
                        } />
                        
                        <Route path="/support" element={
                          <ProtectedRoute>
                            <Support />
                          </ProtectedRoute>
                        } />
                        
                        {/* Referral redirect route - no protection needed */}
                        <Route path="/r/:shortCode" element={<ReferralRedirect />} />
                        
                        {/* Catch all route */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                        </Layout>
                        <EligibilityRefreshProgress />
                        </LazyScholarshipsProvider>
                      </Router>
                        </ThemeProvider>
                      </DebugProvider>
                    </EdvisorProvider>
                      </ProgramCategoriesProvider>
                  </ProgramsProvider>
                </CoursePrerequisiteProvider>
              </PrerequisiteProvider>
            </SchoolsProviderUS>
          </SchoolsProvider>
        </EventsProvider>
      </GuidanceStudentsProvider>
    </OnboardingProvider>
  </UserProvider>
    );
  };

export default App;
